import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Badge } from 'components/badges';
import { Button, PrimaryButton } from 'components/buttons';
import { DebouncedSearchInput, SelectField } from 'components/forms';
import { AlertError, AlertWarning } from 'components/layout/alerts';
import { Skeleton } from 'components/layout/loading';
import {
  setReminderWorker,
  clearError,
  selectRemindersError,
} from 'store/slices/remindersSlice';
import { apiClient, buildQueryString } from 'utils/api';
import { WORKER_TYPES, WORKER_TYPES_LABELS, WORKER_TYPES_OPTIONS } from 'constants/workers';
import { formatSchedule } from 'utils/date';
import { formatReminderActiveWindow } from 'utils/reminders';

const getItemId = (item) => item?._id ?? null;

function WorkerOptionListItem({ worker, isCurrent, isSelected, onSelect }) {
  const isParser = worker.type === WORKER_TYPES.PARSER;
  const typeLabel = WORKER_TYPES_LABELS[worker.type] || worker.type;
  const method = worker.config?.method;
  const url = worker.config?.url;

  return (
    <div
      className={classNames(
        'flex items-start rounded-md border p-4 transition-colors cursor-pointer',
        {
          'border-indigo-500 bg-indigo-900/20': isSelected,
          'border-gray-700 bg-gray-800 hover:bg-gray-700': !isSelected,
        }
      )}
      onClick={onSelect}
    >
      <div
        className={classNames(
          'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
          {
            'border-indigo-500': isSelected,
            'border-gray-500': !isSelected,
          }
        )}
      >
        {isSelected && <div className="h-2 w-2 rounded-full bg-indigo-500" />}
      </div>

      <div className="ml-3 min-w-0 flex-auto">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-medium text-white">{worker.name}</p>
          <Badge size="sm" color="indigo">{typeLabel}</Badge>
          {isCurrent && <Badge size="sm" color="blue">Current worker</Badge>}
        </div>

        <div className="mt-1 flex flex-col gap-y-1 text-xs text-gray-400 sm:flex-row sm:items-center sm:gap-x-2">
          {isParser && url ? (
            <>
              {method && <span className="font-medium text-gray-300">{method}</span>}
              <span className="truncate">{url}</span>
            </>
          ) : (
            <span>{isParser ? 'No endpoint configured' : 'Prebuild worker'}</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AssignWorkerToReminder({
  reminder,
  onSuccess,
  onCancel,
  cancelLabel = 'Cancel',
  successLabel,
  className = '',
}) {
  const dispatch = useDispatch();
  const reminderError = useSelector(selectRemindersError);
  const user = useSelector((state) => state.auth.user);

  const [searchFilterValue, setSearchFilterValue] = useState('');
  const [typeFilterValue, setTypeFilterValue] = useState('ALL');
  const [workers, setWorkers] = useState([]);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [workerSearchError, setWorkerSearchError] = useState(null);
  const [hasFetchedWorkers, setHasFetchedWorkers] = useState(false);
  const [selectedWorkerId, setSelectedWorkerId] = useState(null);
  const [isAttaching, setIsAttaching] = useState(false);
  const workerSearchRequestIdRef = useRef(0);

  const isLoadingWorkerResults = loadingWorkers && hasFetchedWorkers;

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const reminderId = getItemId(reminder);
  const currentWorker = reminder?.worker ?? null;
  const currentWorkerId = getItemId(currentWorker);
  const currentWorkerLabel = currentWorker?.name || (currentWorkerId ? 'Unknown worker' : 'Not assigned');
  const selectedWorker = workers.find((worker) => getItemId(worker) === selectedWorkerId) || null;
  const scheduleText = useMemo(
    () => formatSchedule(reminder, user?.settings, true),
    [reminder, user?.settings]
  );
  const activeWindowText = useMemo(
    () => formatReminderActiveWindow(reminder, user?.settings),
    [reminder, user?.settings]
  );

  const hasSearchQuery = Boolean(searchFilterValue.trim());

  const buildWorkerSearchParams = useCallback((nextState) => {
    const normalizedSearch = nextState.searchFilterValue.trim();

    if (!normalizedSearch) {
      return null;
    }

    const params = { search: normalizedSearch };

    if (nextState.typeFilterValue !== 'ALL') {
      params.type = nextState.typeFilterValue;
    }

    return params;
  }, []);

  const fetchWorkerResults = useCallback(async (nextState) => {
    const requestParams = buildWorkerSearchParams(nextState);
    const requestId = workerSearchRequestIdRef.current + 1;

    workerSearchRequestIdRef.current = requestId;

    if (!requestParams) {
      setHasFetchedWorkers(false);
      setWorkers([]);
      setWorkerSearchError(null);
      setLoadingWorkers(false);
      return;
    }

    setHasFetchedWorkers(true);
    setLoadingWorkers(true);
    setWorkerSearchError(null);

    try {
      const qs = buildQueryString(requestParams);
      const response = await apiClient.get(qs ? `/workers?${qs}` : '/workers');

      if (workerSearchRequestIdRef.current !== requestId) {
        return;
      }

      setWorkers(response.data?.data || []);
    } catch (error) {
      if (workerSearchRequestIdRef.current !== requestId) {
        return;
      }

      setWorkers([]);
      setWorkerSearchError(error.response?.data || 'Failed to fetch workers');
    } finally {
      if (workerSearchRequestIdRef.current === requestId) {
        setLoadingWorkers(false);
      }
    }
  }, [buildWorkerSearchParams]);

  const applyFilters = useCallback((nextPartial) => {
    const hasOwn = (key) => Object.prototype.hasOwnProperty.call(nextPartial, key);
    const nextState = {
      searchFilterValue: hasOwn('searchFilterValue')
        ? nextPartial.searchFilterValue
        : searchFilterValue,
      typeFilterValue: hasOwn('typeFilterValue')
        ? nextPartial.typeFilterValue
        : typeFilterValue,
    };

    if (hasOwn('searchFilterValue')) {
      setSearchFilterValue(nextState.searchFilterValue);
    }

    if (hasOwn('typeFilterValue')) {
      setTypeFilterValue(nextState.typeFilterValue);
    }

    fetchWorkerResults(nextState);
  }, [fetchWorkerResults, searchFilterValue, typeFilterValue]);

  useEffect(() => {
    if (!selectedWorkerId) {
      return;
    }

    const workerStillVisible = hasFetchedWorkers && workers.some((worker) => getItemId(worker) === selectedWorkerId);

    if (!workerStillVisible) {
      setSelectedWorkerId(null);
    }
  }, [hasFetchedWorkers, selectedWorkerId, workers]);

  const replacementWarning = currentWorkerId && selectedWorkerId && currentWorkerId !== selectedWorkerId
    ? `This reminder already has worker "${currentWorker?.name || 'Current worker'}" assigned. Attaching "${selectedWorker?.name}" will replace it.`
    : null;

  const handleAttach = async () => {
    if (!reminderId || !selectedWorkerId) {
      return;
    }

    try {
      setIsAttaching(true);
      await dispatch(setReminderWorker({
        reminderId,
        workerId: selectedWorkerId,
      })).unwrap();

      if (onSuccess) {
        onSuccess();
      }
    } catch (_error) {
      // error is captured in Redux state via rejectWithValue and displayed in the UI
    } finally {
      setIsAttaching(false);
    }
  };

  return (
    <div className={`flex h-full flex-col ${className}`}>
      {reminderError && (
        <div className="mb-4">
          <AlertError title="Error">{reminderError}</AlertError>
        </div>
      )}

      {workerSearchError && (
        <div className="mb-4">
          <AlertError title="Error">{workerSearchError}</AlertError>
        </div>
      )}

      <div className="mb-4 rounded-lg border border-gray-700 bg-gray-800 p-4">
        <h3 className="text-sm font-medium text-gray-300">Selected Reminder</h3>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <p className="text-white font-semibold">{reminder.subject}</p>
          {currentWorkerId && <Badge size="sm" color="blue">Worker assigned</Badge>}
        </div>
        <p className="mt-2 text-sm text-gray-300">{scheduleText}</p>
        {activeWindowText && <p className="mt-1 text-xs text-gray-400">{activeWindowText}</p>}
        <p className="mt-1 text-xs text-gray-400">Current worker: {currentWorkerLabel}</p>
      </div>

      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-end">
        <DebouncedSearchInput
          id="assign-worker-search"
          label="Search workers"
          placeholder="Search workers..."
          value={searchFilterValue}
          onChange={(event) => applyFilters({ searchFilterValue: event.target.value })}
          disabled={isAttaching}
          className="w-full sm:flex-1"
        />
        <SelectField
          label="Type"
          className="w-full sm:w-48 sm:shrink-0"
          options={[
            { value: 'ALL', label: 'All' },
            ...WORKER_TYPES_OPTIONS,
          ]}
          value={typeFilterValue}
          onChange={(value) => applyFilters({ typeFilterValue: value })}
          disabled={isAttaching}
        />
      </div>

      <div className="custom-scrollbar min-h-[200px] max-h-[400px] flex-1 space-y-4 overflow-y-auto pr-2">
        {isLoadingWorkerResults && !isAttaching ? (
          <>
            <Skeleton className="h-16 w-full rounded-md" />
            <Skeleton className="h-16 w-full rounded-md" />
            <Skeleton className="h-16 w-full rounded-md" />
          </>
        ) : (
          <>
            {hasFetchedWorkers && workers.map((worker) => {
              const workerId = getItemId(worker);

              return (
                <WorkerOptionListItem
                  key={workerId}
                  worker={worker}
                  isCurrent={workerId === currentWorkerId}
                  isSelected={workerId === selectedWorkerId}
                  onSelect={() => setSelectedWorkerId(workerId)}
                />
              );
            })}

            {!hasFetchedWorkers && (
              <p className="py-8 text-center text-gray-400">
                Search for workers to see results.
              </p>
            )}

            {hasFetchedWorkers && workers.length === 0 && (
              <p className="py-8 text-center text-gray-400">
                {hasSearchQuery ? 'No matching workers found.' : 'Search for workers to see results.'}
              </p>
            )}
          </>
        )}
      </div>

      {replacementWarning && (
        <div className="mt-4">
          <AlertWarning>{replacementWarning}</AlertWarning>
        </div>
      )}

      <div className="mt-6 flex flex-col gap-y-2">
        <PrimaryButton
          onClick={handleAttach}
          disabled={!selectedWorkerId || isLoadingWorkerResults || isAttaching}
          className="w-full"
        >
          {isAttaching
            ? 'Attaching...'
            : (successLabel || (selectedWorkerId ? 'Attach & Finish' : 'Finish'))}
        </PrimaryButton>

        {onCancel && (
          <Button
            onClick={onCancel}
            className="w-full"
            disabled={isAttaching}
          >
            {cancelLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
